/**
 * Controls for requesting audio generation for all script lines in a SIM level.
 *
 * @module components/sim/createLevels/GenerateAudio
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Divider,
    Typography,
    Button,
    Select,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import {useCharacterDataStyle} from './CharacterData';

/**
 * GenerateAudio is a React functional component that manages the audio generation and playback functionality for a character.
 *
 * The component handles the interaction of generating, switching, and recording audio for a character.
 * It supports both recorded and synthesized audio, allowing users to toggle between them
 * and perform specific actions like selecting voices or playing audio tracks.
 *
 * @param {Object} props The props object for the GenerateAudio component.
 * @param {Object} props.data Contains character data, including audio-related properties.
 * @param {Object} props.selectedCharacter Represents the currently selected character with its type.
 * @param {Function} props.setSwitchGenerate The function to toggle the `switchGenerate` state.
 * @param {boolean} props.switchGenerate The state that determines the switch between audio modes.
 * @param {Function} props.handleAgentRecordedAudio Callback function executed for managing agent-recorded audio.
 * @param {number} props.index Index of the character or item being processed.
 * @param {Function} props.updateData Callback function for updating data after interactions.
 * @param {Function} props.handleAudioChange Callback triggered when audio state changes.
 * @param {boolean} props.genAudio State flag controlling the audio generation process.
 * @param {Function} props.setVoice Callback function to update the selected voice.
 * @param {Array} props.currentVoice Contains the currently active voice options available for selection.
 * @param {Function} props.setGenAudio Toggles the state to control the generation of audio.
 * @param {Object} props.node A React reference object used for DOM manipulation.
 * @param {string} props.voice The currently selected voice for audio generation.
 * @param {Object} props.newSim Provides voice selection data for simulation.
 * @param {Function} props.showPlayAudio Callback function to manage audio playback.
 *
 * @returns {JSX.Element} A React component that renders the UI for managing audio generation, playback, and voice selection.
 */
const GenerateAudio = ({
    data,
    selectedCharacter,
    setSwitchGenerate,
    switchGenerate,
    handleAgentRecordedAudio,
    index,
    updateData,
    handleAudioChange,
    genAudio,
    setVoice,
    currentVoice,
    setGenAudio,
    node,
    voice,
    newSim,
    showPlayAudio,
}) => {
    const classes = useCharacterDataStyle();

    /**
     * Updates the agent's recorded audio information and triggers associated state updates.
     *
     * @function setAgentRecordedAudio
     * @param {string} record - The recorded audio data for the agent.
     */
    const setAgentRecordedAudio = (record) => {
        data.agent_recorded_audio = record;
        setSwitchGenerate(!switchGenerate);
        handleAgentRecordedAudio(index);
        updateData(index, data);
    };

    /**
     * Toggles the generation of audio and updates the relevant state and data.
     *
     * This function is responsible for handling the change in audio generation state.
     * It performs the following operations:
     * - Updates the audio state by invoking the `handleAudioChange` function with the provided index.
     * - Modifies the relevant data by calling the `updateData` method with the given index and current data.
     * - Toggles the switchGenerate state to reflect the new state of audio generation.
     *
     * The function assumes the existence of the `index` variable, the `data` object,
     * and the `switchGenerate` state in its enclosing scope.
     */
    const switchGenerateAudio = () => {
        handleAudioChange(index);
        updateData(index, data);
        setSwitchGenerate(!switchGenerate);
    };

    /**
     * Function to initiate the generation process.
     *
     * This function sets the voice to the first available voice from the
     * currentVoice array and triggers the generation of audio.
     */
    const generate = () => {
        setVoice(currentVoice[0]);
        setGenAudio(true);
    };

    /**
     * Handles changes to the voice selection in a voice settings interface.
     *
     * This function is typically used as an event handler for a change event
     * on a select input or similar control. When the event is triggered, it
     * updates the selected voice setting to the value provided by the event.
     *
     * @param {Event} event - The event object triggered by the voice change action.
     *                        `event.target.value` contains the new voice value.
     */
    const handleVoiceChange = (event) => {
        setVoice(event.target.value);
    };

    /**
     * Determines and renders the interface for generating or switching audio based on the character type
     * and the current state of audio attributes.
     *
     * @function showGenerateAudio
     * @param {boolean} switchAudio - Indicates whether the recorded audio is currently being used or switched.
     * @returns {JSX.Element|null} - The component structure displaying buttons, audio controls, and options for
     *                               generating or managing audio. Returns null if no conditions are met.
     *
     * This function dynamically generates a UI to manage audio generation, switching between generated
     * audio and recorded audio, and the ability to select specific voice options. The UI behavior changes
     * depending on the type of character ('agent') and the states of `data.audio_url`,
     * `data.agent_recorded_audio`, and `switchAudio`.
     */
    const showGenerateAudio = (switchAudio) => {
        if (
            selectedCharacter.type === 'agent'
            && data.audio_url
            && !data.agent_recorded_audio
        ) {
            return (
                <div className={classes.generateDiv}>
                    <Button className={classes.switchBtn} onClick={() => setAgentRecordedAudio(false)}>
                        <Typography className={classes.promptRecord}>
                            Prompt to record:&nbsp;
                        </Typography>
                        SWITCH
                    </Button>
                </div>
            );
        }
        if (selectedCharacter.type === 'agent' && data.agent_recorded_audio) {
            return (
                <div className={classes.generateDiv}>
                    <Button
                        className={classes.recordSwitchBtn}
                        onClick={() => switchGenerateAudio(true)}
                    >
                        <Typography className={classes.recordedAudio}>
                            Recorded Audio:
                        </Typography>
                        <span className={classes.switch}>SWITCH</span>
                    </Button>
                    <Divider/>
                    {
                        genAudio ? (
                                <div className={classes.selectDiv} ref={node}>
                                    <Select
                                        variant="outlined"
                                        name="voice"
                                        size="small"
                                        className={classes.select}
                                        value={voice}
                                        onChange={handleVoiceChange}
                                        native
                                    >
                                        {Object.entries(newSim.selectVoices).map(([key, value]) => (
                                            <option value={key} key={key} className={classes.descriptionLabel}>
                                                {value}
                                            </option>
                                        ))}
                                    </Select>
                                    <CheckBoxIcon className={classes.chckboxIcn} onClick={showPlayAudio}/>
                                </div>
                            ) :
                            (
                                <Button className={classes.generateAudioBtn} onClick={generate}>
                                    GENERATE AUDIO
                                </Button>
                            )
                    }
                </div>
            );
        }
        if (selectedCharacter.type === 'agent' && switchAudio) {
            return (
                <div className={classes.generateDiv}>
                    <Button className={classes.switchBtn} onClick={() => setAgentRecordedAudio(false)}>
                        <Typography className={classes.promptRecord}>
                            Prompt to record:&nbsp;
                        </Typography>
                        SWITCH
                    </Button>
                </div>
            );
        }
        if (selectedCharacter.type === 'agent' && !switchAudio) {
            return (
                <div className={classes.generateDiv}>
                    <Button
                        className={classes.recordSwitchBtn}
                        onClick={() => switchGenerateAudio(true)}
                    >
                        <Typography className={classes.recordedAudio}>
                            Recorded Audio:
                        </Typography>
                        <span className={classes.switch}>SWITCH</span>
                    </Button>
                    <Divider/>
                    <Button className={classes.generateAudio} onClick={showPlayAudio}>
                        GENERATE AUDIO
                    </Button>
                </div>
            );
        }
        if (selectedCharacter.type !== 'agent' && !switchAudio) {
            return (
                <div className={classes.generateDiv}>
                    <Button className={classes.generateAudioBtn} onClick={showPlayAudio}>
                        GENERATE AUDIO
                    </Button>
                </div>
            );
        }
        return (
            <div className={classes.generateDiv}>
                {
                    genAudio ? (
                            <div className={classes.selectDiv} ref={node}>
                                <Select
                                    variant="outlined"
                                    name="voice"
                                    size="small"
                                    className={classes.select}
                                    value={voice}
                                    onChange={handleVoiceChange}
                                    native
                                >
                                    {Object.entries(newSim.selectVoices).map(([key, value]) => (
                                        <option value={key} key={key} className={classes.descriptionLabel}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                                <CheckBoxIcon className={classes.chckboxIcn} onClick={showPlayAudio}/>
                            </div>
                        ) :
                        (
                            <Button className={classes.generateAudioBtn} onClick={generate}>
                                GENERATE AUDIO
                            </Button>
                        )
                }
            </div>
        );
    };

    return (
        <Typography component="span" style={{marginLeft: 'auto'}}>
            {showGenerateAudio(!switchGenerate)}
        </Typography>
    );
};

GenerateAudio.propTypes = {
    index: PropTypes.number,
    data: PropTypes.object,
    selectedCharacter: PropTypes.object,
    setSwitchGenerate: PropTypes.func,
    switchGenerate: PropTypes.bool,
    handleAgentRecordedAudio: PropTypes.func,
    updateData: PropTypes.func,
    handleAudioChange: PropTypes.func,
    genAudio: PropTypes.bool,
    setVoice: PropTypes.func,
    currentVoice: PropTypes.any,
    setGenAudio: PropTypes.func,
    node: PropTypes.object,
    voice: PropTypes.string,
    newSim: PropTypes.object,
    showPlayAudio: PropTypes.func,
};
export default GenerateAudio;
